'use client';

import React from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';

const Footer = () => {
  return (
    <footer
      className="p-4 w-full pt-8 mx-auto md:px-24 lg:px-8"
      style={{
        background: '#ffffff',
        color: '#000000',
        // Creates a subtle shadow above the footer
        boxShadow: '0 -3px 8px rgba(0, 0, 0, 0.1)',
        fontFamily: 'Poppins, Arial, sans-serif',
      }}
    >
      <div className="grid gap-10 row-gap-6 mb-8 sm:grid-cols-2 lg:grid-cols-4">
        {/* LOGO + SHORT DESC */}
        <div className="sm:col-span-2">
          <a
            href="/"
            aria-label="Go home"
            title="Company"
            className="inline-flex items-center hover:opacity-90 transition-opacity duration-300"
          >
            <img src="/monogram.webp" alt="Logo" className="h-10 w-auto" />
            <span className="ml-2 text-xl font-bold uppercase tracking-wide">
              Data Pulse Technologies
            </span>
          </a>
          <div className="mt-6 lg:max-w-sm text-sm leading-relaxed">
            <p>
              We transform your visions into reality at Data Pulse Technologies.
              Our dynamic team of full-stack developers doesn’t just work for you;
              we work <em>with</em> you, ensuring every solution perfectly
              aligns with your goals and visions.
            </p>
          </div>
        </div>

        {/* CONTACT US */}
        <div className="space-y-3 text-sm leading-relaxed">
          <p className="text-base font-bold tracking-wide">Contact Us</p>

          {/* Email */}
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faEnvelope}
              className="h-5 w-5 hover:scale-110 transition-transform"
            />
            <a
              href="mailto:info@datapulsetechnologies.org"
              aria-label="Our email"
              title="Our email"
              className="hover:underline transition-colors"
            >
              info@datapulsetechnologies.org
            </a>
          </div>

          {/* Address */}
          <div className="flex items-start gap-2">
            <FontAwesomeIcon
              icon={faMapMarkerAlt}
              className="h-5 w-5 hover:scale-110 transition-transform"
            />
            <a
              href="https://www.google.com/maps"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Our address"
              title="Our address"
              className="hover:underline transition-colors"
            >
              317 Blarney Golf Resort
              <br />
              Tower, Co. Cork
              <br />
              T23RK05
              <br />
              Ireland
            </a>
          </div>
        </div>

        {/* SOCIALS */}
        <div>
          <p className="text-base font-bold tracking-wide mb-2">Social</p>
          <div className="flex items-center space-x-4">
            {/* LinkedIn */}
            <a
              href="https://www.linkedin.com/company/data-pulse-technologies/"
              className="transition-transform duration-300 hover:scale-110"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                <path
                  d="M21.4,0H2.6C1.2,0,0,1.2,0,2.6v18.8C0,22.8,1.2,24,2.6,24h18.8
                     c1.4,0,2.6-1.2,2.6-2.6V2.6C24,1.2,22.8,0,21.4,0z M7.9,20.4H4.5V9.3h3.4V20.4z
                     M6.2,8.2c-1.1,0-1.9-0.7-1.9-1.5
                     c0-0.8,0.7-1.5,1.8-1.5c1.1,0,1.9,0.7,1.9,1.5C8.1,7.5,7.4,8.2,6.2,8.2z
                     M21.5,20.4h-3.4v-5.6c0-1.3,0-3-1.8-3
                     c-1.8,0-2,1.4-2,2.8v5.8h-3.4V9.3h3.2v1.5h0.1
                     c0.5-0.9,1.8-1.9,3.1-1.9c3.4,0,4,2.2,4,5V20.4z"
                />
              </svg>
            </a>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/datapulsetechnologies/"
              className="transition-transform duration-300 hover:scale-110"
            >
              <svg viewBox="0 0 30 30" fill="currentColor" className="h-6 w-6">
                <circle cx="15" cy="15" r="4" />
                <path
                  d="M19.999,3h-10C6.14,3,3,6.141,3,10.001v10C3,23.86,6.141,27,
                     10.001,27h10C23.86,27,27,23.859,27,
                     19.999v-10C27,6.14,23.859,3,19.999,3z
                     M15,21c-3.309,0-6-2.691-6-6s2.691-6,6-6s6,
                     2.691,6,6S18.309,21,15,21z M22,9c-0.552,0-1-0.448-1-1
                     c0-0.552,0.448-1,1-1s1,0.448,1,1C23,8.552,22.552,9,22,9z"
                />
              </svg>
            </a>

            {/* Facebook */}
            <a
              href="https://www.facebook.com/people/Data-Pulse-technologies/61556207078422/"
              className="transition-transform duration-300 hover:scale-110"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                <path
                  d="M22,0H2C0.895,0,0,0.895,0,2v20c0,
                     1.105,0.895,2,2,2h11v-9h-3v-4h3V8.413
                     c0-3.1,1.893-4.788,4.659-4.788
                     c1.325,0,2.463,0.099,2.795,0.143v3.24
                     l-1.918,0.001c-1.504,0-1.795,0.715-1.795,1.763V11
                     h4.44l-1,4h-3.44v9H22c1.105,0,2-0.895,
                     2-2V2C24,0.895,23.105,0,22,0z"
                />
              </svg>
            </a>
          </div>
          <p className="mt-4 text-sm">
            Connect, Share, Thrive: Explore our Socials!
          </p>
        </div>
      </div>

      {/* COPYRIGHT + LEGAL LINKS */}
      <div className="flex flex-col-reverse justify-between pt-5 pb-10 border-t border-black/20 lg:flex-row">
        <p className="text-xs sm:text-sm mt-4 lg:mt-0">
          © 2024 Data Pulse Technologies Inc. All rights reserved.
        </p>
        <ul className="flex flex-col mb-3 space-y-2 lg:mb-0 sm:space-y-0 sm:space-x-5 sm:flex-row text-sm">
          <li>
            <a
              href="/"
              className="transition-colors duration-300 hover:underline"
            >
              F.A.Q
            </a>
          </li>
          <li>
            <a
              href="/"
              className="transition-colors duration-300 hover:underline"
            >
              Privacy Policy
            </a>
          </li>
          <li>
            <a
              href="/"
              className="transition-colors duration-300 hover:underline"
            >
              Terms &amp; Conditions
            </a>
          </li>
        </ul>
      </div>
    </footer>
  );
};

export default Footer;
